import { useState } from "react";
import { toast } from "sonner";
import { FolderPlus, Pencil, Trash2, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

export function FolderManager({ folders, onChange, videoCounts }) {
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // {id, name}
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/folders", { name: newName.trim() });
      onChange([...folders, data]);
      setNewName("");
      toast.success(`Folder "${data.name}" created.`);
    } catch {
      toast.error("Could not create folder.");
    } finally {
      setBusy(false);
    }
  };

  const rename = async () => {
    if (!editing?.name.trim()) return;
    try {
      const { data } = await api.put(`/admin/folders/${editing.id}`, { name: editing.name.trim() });
      onChange(folders.map((f) => (f.id === data.id ? data : f)));
      setEditing(null);
      toast.success("Folder renamed.");
    } catch {
      toast.error("Rename failed.");
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}"? Videos inside stay on the wall, just unfiled.`)) return;
    try {
      await api.delete(`/admin/folders/${f.id}`);
      onChange(folders.filter((x) => x.id !== f.id));
      toast.success("Folder deleted — its videos are now unfiled.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="border border-cream-dark bg-white p-6" data-testid="folder-manager">
      <p className="eyebrow text-ink-soft mb-4">Folders — organize the wall</p>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {folders.length === 0 && <p className="text-sm text-ink-soft">No folders yet. Create one (e.g. "Beauty Ads", "Testimonials") then file videos into it.</p>}
        {folders.map((f) => (
          <span key={f.id} data-testid={`folder-chip-${f.id}`} className="inline-flex items-center gap-2 border border-cream-deeper px-3 py-1.5 text-xs uppercase tracking-[0.12em] font-semibold text-ink">
            {editing?.id === f.id ? (
              <>
                <Input value={editing.name} onChange={(e) => setEditing({ id: f.id, name: e.target.value })}
                  className="h-7 w-32 rounded-none text-xs" data-testid={`folder-rename-input-${f.id}`} autoFocus />
                <button onClick={rename} data-testid={`folder-rename-save-${f.id}`} aria-label="Save name" className="text-ink"><Check className="w-3.5 h-3.5" /></button>
                <button onClick={() => setEditing(null)} aria-label="Cancel" className="text-ink-soft"><X className="w-3.5 h-3.5" /></button>
              </>
            ) : (
              <>
                {f.name} <span className="text-ink-soft/60 normal-case">({videoCounts[f.id] || 0})</span>
                <button onClick={() => setEditing({ id: f.id, name: f.name })} data-testid={`folder-edit-${f.id}`} aria-label={`Rename ${f.name}`} className="text-ink-soft hover:text-ink"><Pencil className="w-3 h-3" /></button>
                <button onClick={() => remove(f)} data-testid={`folder-delete-${f.id}`} aria-label={`Delete ${f.name}`} className="text-ink-soft hover:text-ink"><Trash2 className="w-3 h-3" /></button>
              </>
            )}
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder="New folder name" className="rounded-none h-10 max-w-xs" data-testid="folder-new-input" />
        <button onClick={create} disabled={busy || !newName.trim()} data-testid="folder-create-button"
          className="inline-flex items-center gap-2 bg-ink hover:bg-neutral-800 disabled:opacity-50 text-cream px-5 h-10 text-xs uppercase tracking-[0.14em] font-semibold transition-colors">
          <FolderPlus className="w-4 h-4" /> Create
        </button>
      </div>
    </div>
  );
}
